import React, { memo, useEffect, useState } from 'react';
// import '@style/components/card.scss';
// import '@style/modules/insights/guideDrawer.scss';
// import '@style/modules/insights/guideDrawerContent.scss';
import '../../styles/components/card.scss';
import '../../styles/modules/insights/guideDrawer.scss';
import '../../styles/modules/insights/guideDrawerContent.scss';
import { Button } from 'antd';
import RichEditor from '@modules/questionnaires/complexity/richEditor';
import { Dropdown } from '@modules/insights/drawerDropdown';
import actions from '@flux/action';
import { useDispatch, useSelector } from 'react-redux';
import useTheme from '@hooks/useTheme';
import trashIcon from '@assets/imgs/trash.png';
import cancelIcon from '@assets/imgs/cancel.png';
import yesIcon from '@assets/imgs/yes.png';
import trashLightIcon from '@assets/imgs/trash-light.png';
import cancelLightIcon from '@assets/imgs/cancel-light.png';
import yesLightIcon from '@assets/imgs/yes-light.png';
import {
  customDeleteConfirm,
  noSaveConfirm,
} from '@components/dragableTableFrom/formItems';
import { projectRole } from '@model';

const genExpandedClassName = (a, b) =>
  b ? ' full-expanded' : a ? ' expanded' : '';

export const ExpandBtnRight = ({
  handleToggleExpand,
  isExpand = false,
  text = 'Discussion Guide',
}) => (
  <div
    className={`expand-btn-right${genExpandedClassName(isExpand)}`}
    onClick={() => handleToggleExpand((isExpanded) => !isExpanded)}
  >
    <div className="top">
      <div />
    </div>
    <div className="bottom">
      <div />
    </div>
    <span className="text"> {text}</span>
    <span
      className={`appkiticon icon-${isExpand ? 'up' : 'down'}-chevron-fill`}
    />
    <div className="button-shadow-cover" />
  </div>
);

const FullExpandBtnRight = ({ handleToggleFullExpand, isExpand = false }) => (
  <div className="toggle-tile" onClick={handleToggleFullExpand}>
    <span
      className={`appkiticon icon-${
        isExpand ? 'right' : 'left'
      }-chevron-fill a-font-16 a-text-orange`}
    />
    <span className="text">{isExpand ? 'Collapse' : 'Expand'}</span>
  </div>
);

const GuideDrawer = ({
  isExpand,
  onExpand,
  isFullExpand,
  setIsFullExpand,
  drawerData = [],
  projectId,
  type,
  companyInstance,
  isLeftPartEditing,
}) => {
  const dispatch = useDispatch();
  const [forceUpdateToggle, setForceUpdateToggle] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [currentSectionId, setCurrentSectionId] = useState(drawerData[0]?.id);
  const [title, setTitle] = useState();
  const [body, setBody] = useState();

  const [prevBody, setPrevBody] = useState();
  const [prevTitle, setPrevTitle] = useState();
  const [fallBacks, setFallbacks] = useState({
    id: drawerData[0]?.id,
    title: drawerData[0]?.title,
    body: drawerData[0]?.discussionPoint,
  });
  const [error, setError] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  // const isInternal = JSON.parse(window.localStorage.userInfo || '{}').isInternal;
  const isClient = JSON.parse(window.localStorage.userInfo || '{}')?.isClient;
  const projectPermission = useSelector(
    (state) => state.common.projectCompanyInfo?.currentUserPermission,
  );
  const isPwcReadOnly = projectPermission === projectRole['View-Only'];

  const guidingPrinciplesType = type !== 'Customize' ? `${type} ` : '';

  let guidingPrinciplesHeaderType = guidingPrinciplesType;
  const customizedModules = useSelector(
    (state) => state.projects.customizedModules,
  );

  if (customizedModules.some((x) => x.module === 'SMO Launch')) {
    guidingPrinciplesHeaderType = 'Separation ';
  }

  // handle dropdown selection change
  useEffect(() => {
    if (currentSectionId?.startsWith('gd_')) {
      // if you get here from an <add new> action, return out of it
      return;
    }

    const currentSection = drawerData.find((e) => e.id === currentSectionId);
    const currentIndex = drawerData.findIndex((e) => e.id === currentSectionId);

    if (currentIndex !== drawerData.length - 1) {
      // if selected option is NOT the last option, set the fallback to the next one
      setFallbacks({
        id: drawerData[currentIndex + 1].id,
        title: drawerData[currentIndex + 1].title,
        body: drawerData[currentIndex + 1].discussionPoint,
      });
    } else {
      // if selected option IS the last option, set the fallback to the first one
      setFallbacks({
        id: drawerData[0]?.id,
        title: drawerData[0]?.title,
        body: drawerData[0]?.discussionPoint,
      });
    }
    const { title, discussionPoint: data } = currentSection || {};

    setForceUpdateToggle(!forceUpdateToggle);

    setTitle(title);
    setBody(data);

    setPrevTitle(title);
    setPrevBody(data);
  }, [currentSectionId]);

  /**
   * handle choose <add new> from the dropdown
   */
  useEffect(() => {
    if (currentSectionId?.startsWith('gd_')) {
      // Add new entry
      setTitle('New Point');
      setBody('');
      setReadOnly(false);
    }
  }, [currentSectionId]);

  /**
   * handle add/delete update current selected option
   */
  useEffect(() => {
    const lastValidOptionIndex = drawerData.length - 1;
    if (currentSectionId === null) {
      // add/delete action
      setCurrentSectionId(drawerData[lastValidOptionIndex].id);
    }
  }, [drawerData]);

  function checkError() {
    return !title;
  }

  async function __delete() {
    if (isSaving) {
      return;
    }
    const { id, title, body } = fallBacks;
    if (currentSectionId) {
      setIsSaving(true);
      await dispatch(
        actions.deleteGdPrincipleDrawerData(
          projectId,
          currentSectionId,
          companyInstance,
        ),
      );
      setIsSaving(false);
    }
    setCurrentSectionId(id);
    setTitle(title);
    setBody(body);
    setReadOnly(true);
  }

  function __cancel() {
    setReadOnly(true);
    setBody(prevBody);
    setTitle(prevTitle);
  }

  async function __update() {
    let id = currentSectionId;
    if (currentSectionId.startsWith('gd_')) {
      id = null;
      setCurrentSectionId(null);
    }
    await dispatch(
      actions.updateGdPrincipleDrawerData(
        {
          discussionGuideInfo: {
            id,
            title,
            discussionPoint: body,
          },
        },
        projectId,
        companyInstance,
      ),
    );
  }

  function handleDelete() {
    setError('');
    if (currentSectionId.startsWith('gd_')) {
      handleCancel();
      return;
    }
    if (drawerData.length === 1) {
      customDeleteConfirm({
        customContent: 'There must exist at least one discussion point.',
        customOK: 'ok',
      });
    } else {
      customDeleteConfirm({
        onOk: __delete,
        customContent: `This will delete the full content of ${title}, do you want to proceed?`,
      });
    }
  }

  function handleCancel() {
    setError('');
    noSaveConfirm({ onOk: __cancel });
  }

  async function handleToggle() {
    if (isSaving) {
      return;
    }
    const readWrite = !readOnly;
    if (readWrite) {
      if (checkError()) {
        setError('* The title is required');
        return;
      }
      setError('');
      setPrevBody(body);
      setPrevTitle(title);
      setIsSaving(true);
      await __update();
      setIsSaving(false);
    }
    setReadOnly(readWrite);
  }

  const editingArea = (
    <>
      {readOnly ? (
        <>
          <Button
            type="primary"
            onClick={handleToggle}
            disabled={drawerData?.length === 0 || !drawerData}
          >
            Edit
          </Button>
        </>
      ) : (
        <>
          <div className="icon-container" onClick={handleDelete}>
            <img
              alt="delete icon"
              src={isLightMode ? trashLightIcon : trashIcon}
            />
          </div>
          <div className="icon-container" onClick={handleCancel}>
            <img
              alt="cancel icon"
              src={isLightMode ? cancelLightIcon : cancelIcon}
            />
          </div>
          <div className="icon-container" onClick={handleToggle}>
            <img alt="toggle icon" src={isLightMode ? yesLightIcon : yesIcon} />
          </div>
        </>
      )}
    </>
  );
  return (
    <>
      <ExpandBtnRight isExpand={isExpand} handleToggleExpand={onExpand} />
      <div
        className={`expand-guiding-principle${genExpandedClassName(
          isExpand,
          isFullExpand,
        )}`}
      >
        <FullExpandBtnRight
          isExpand={isFullExpand}
          handleToggleFullExpand={() => {
            setIsFullExpand((v) => !v);
          }}
        />

        <div className="collapse-header">
          <span>{`${guidingPrinciplesHeaderType}Guiding Principles`}</span>
        </div>

        <div className="collapse-purpose">
          <i>Purpose:</i>{' '}
          {`Discussion guide for key executives to assist in
          converting acquisition strategy into ${guidingPrinciplesType.toLowerCase()}strategy. The output
          of this document should be shared with the ${guidingPrinciplesType}Organization to
          guide ${guidingPrinciplesType.toLowerCase()}planning and execution.`}
          <br />
          <br />
          <div className="points-of-discussion">
            <div>
              <span>
                <b>Points of Discussion:</b>
              </span>
            </div>
            <div className="drawer-buttons role-control">
              {!isLeftPartEditing ? editingArea : null}
            </div>
          </div>
        </div>

        <div className="gp-drawer-controller">
          {/* use ternary to prevent empty dropdown title when initializing*/}
          <Dropdown
            title={title}
            list={
              isClient || isPwcReadOnly || isLeftPartEditing
                ? drawerData
                : drawerData.concat({ id: null, title: '+ Add New Point' })
            }
            sendData={setCurrentSectionId}
            isViewMode={readOnly}
            onTitleChange={setTitle}
            isLeftPartEditing={isLeftPartEditing}
          />
          <div className="error">{error}</div>
        </div>
        <div className="gp-drawer-separator" />
        <div className={`gp-drawer-content${readOnly ? '' : '-edit'}`}>
          {/* use forceUpdateToggle to let richEditor.js\'s useEffect re-render content when 'body' changed by dropdown */}
          <RichEditor
            readOnly={readOnly}
            onInitialContentChange={forceUpdateToggle}
            value={body}
            onChange={(e) => setBody(e)}
            autoSize={{ minRows: 3, maxRows: 3 }}
            style={{ resize: 'none' }}
          />
        </div>
      </div>
    </>
  );
};

export default memo(GuideDrawer);
